import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowUpRight, Quote } from "lucide-react";
import { CURRICULUM } from "@/lib/curriculum";
import { posterFor } from "@/lib/journey";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ago = (iso) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "a month ago" : `${months} months ago`;
};

function GradCard({ g, i }) {
  const meta = [
    g.days_in ? `${g.days_in} day${g.days_in === 1 ? "" : "s"} in` : null,
    g.streak_longest >= 7 ? `${g.streak_longest}-day streak` : null,
    g.approx ? null : ago(g.graduated_at),
  ].filter(Boolean).join(" · ");
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.65, 0, 0.35, 1] }}
      className="relative overflow-hidden border border-[#E7DFD5] bg-[#FFFFFF] p-5 hover:border-[#F59E0B]/50 transition-colors duration-500"
      data-testid={`graduate-card-${i}`}
    >
      <div className="grid grid-cols-8 gap-1 mb-4">
        {CURRICULUM.map((m) => (
          <div key={m.id} className="relative aspect-[3/4] overflow-hidden">
            <img src={posterFor(m.id)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80" />
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-gradient-to-r from-red-500/80 via-[#F59E0B]/80 to-emerald-400/80 mb-4" />
      {g.photo && (
        <img
          src={g.photo}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          className="w-11 h-11 rounded-full object-cover border border-[#F59E0B]/50 mb-3"
          data-testid={`graduate-card-photo-${i}`}
        />
      )}
      <div className="font-display text-3xl leading-none text-[#1C1917]">Through it.</div>
      {g.name && (
        <div className="font-display italic text-lg text-[#B45309] mt-2" data-testid={`graduate-card-name-${i}`}>— {g.name}</div>
      )}
      {g.voice && (
        <p className="text-sm text-[#44403C] italic leading-relaxed mt-3 border-l-2 border-emerald-400/50 pl-3" data-testid={`graduate-card-quote-${i}`}>
          “{g.voice.quote}”<span className="block not-italic font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C] mt-1">— {[g.voice.name, g.voice.role].filter(Boolean).join(", ")}</span>
        </p>
      )}
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#78716C] mt-3" data-testid={`graduate-card-meta-${i}`}>
        Graduate{meta ? ` · ${meta}` : ""}
      </div>
    </motion.article>
  );
}

export const GraduateWall = () => {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/graduates/wall`).then((r) => setData(r.data)).catch(() => setData({ count: 0, graduates: [] }));
  }, []);

  useEffect(() => {
    if (data?.count && window.location.hash === "#graduate-wall") {
      setTimeout(() => document.getElementById("graduate-wall")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [data]);

  if (!data || (!data.count && !(data.voices || []).length)) return null;
  const voices = data.voices || [];
  const LIMIT = 9;
  const shown = showAll ? voices : voices.slice(0, LIMIT);

  return (
    <section id="graduate-wall" className="relative bg-[#FAF7F2] py-28 md:py-36 border-t border-[#E7DFD5]" data-testid="graduate-wall">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="section-tag mb-4 flex items-center gap-2"><GraduationCap size={14} /> § The Graduate Wall</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
              They went through <span className="italic text-emerald-300">all eight</span>.
            </h2>
          </div>
          {data.count > 0 && (
            <div className="md:text-right">
              <div className="font-display text-5xl text-[#B45309] leading-none" data-testid="graduate-wall-count">{data.count}</div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] mt-2">
                {data.count === 1 ? "member has" : "members have"} finished the Formula to Healing. Anonymous unless they chose otherwise.
              </div>
            </div>
          )}
        </div>
        {data.graduates.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.graduates.map((g, i) => <GradCard key={`${g.graduated_at}-${i}`} g={g} i={i} />)}
          </div>
        )}
        {voices.length > 0 && (
          <div className="mt-14" data-testid="wall-voices">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div className="section-tag flex items-center gap-2"><Quote size={13} /> § In their words</div>
              <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#78716C]" data-testid="wall-voices-count">
                {voices.length} voice{voices.length === 1 ? "" : "s"} · every approved story stays here
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shown.map((v, i) => (
                <motion.blockquote
                  key={`${v.at}-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.65, 0, 0.35, 1] }}
                  className={`relative border bg-[#FFFFFF] p-6 transition-colors duration-500 ${v.current ? "border-emerald-400/60" : "border-[#E7DFD5] hover:border-emerald-400/40"}`}
                  data-testid={`wall-voice-${i}`}
                  data-current={v.current ? "true" : "false"}
                >
                  {v.current && (
                    <span className="absolute top-3 right-3 font-mono text-[8px] tracking-[0.25em] uppercase text-black bg-emerald-300 px-2 py-1" data-testid={`wall-voice-current-${i}`}>This week</span>
                  )}
                  <p className="font-display text-xl md:text-2xl leading-snug tracking-tight text-[#1C1917] pr-16">“{v.quote}”</p>
                  <footer className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#78716C] mt-4" data-testid={`wall-voice-name-${i}`}>
                    — {[v.name, v.role].filter(Boolean).join(", ")}{v.graduate ? <span className="text-emerald-300"> · graduate</span> : null}
                  </footer>
                </motion.blockquote>
              ))}
            </div>
            {voices.length > LIMIT && (
              <button onClick={() => setShowAll((s) => !s)} className="btn-outline text-xs mt-6" data-testid="wall-voices-more">
                {showAll ? "Show fewer" : `Show all ${voices.length} voices`}
              </button>
            )}
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#555] mt-4">From members' own journals — shared with their permission, signed the way they chose. The one marked “This week” is on the front page.</p>
          </div>
        )}
        <div className="mt-12 flex flex-wrap items-center gap-6">
          <Link to="/register" className="btn-ember text-sm" data-testid="graduate-wall-cta">
            Start your eight <ArrowUpRight size={16} />
          </Link>
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C] max-w-sm">
            Every card here is a real member who ticked all eight modules. Yours goes up the day you finish.
          </p>
        </div>
      </div>
    </section>
  );
};
